import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface HeroProps {
  title: string;
  subtitle?: string;
  description?: string;
  children?: ReactNode;
  align?: 'left' | 'center';
  size?: 'sm' | 'md' | 'lg' | 'full';
  className?: string;
  badge?: string;
  highlightWord?: string;
  variant?: 'light' | 'dark';
}

export function Hero({
  title,
  subtitle,
  description,
  children,
  align = 'center',
  size = 'md',
  className,
  badge,
  highlightWord,
  variant = 'light',
}: HeroProps) {
  // ninito exact padding values
  const sizes = {
    sm: 'pt-32 pb-16 md:pt-36 md:pb-20',
    md: 'pt-36 pb-20 md:pt-40 md:pb-24',
    lg: 'pt-40 pb-24 md:pt-48 md:pb-32',
    full: 'min-h-screen flex flex-col justify-center pt-20',
  };

  const isLight = variant === 'light';

  // Function to highlight a specific word in blue
  const renderTitle = () => {
    if (!highlightWord) return title;

    const parts = title.split(highlightWord);
    if (parts.length === 1) return title;

    return (
      <>
        {parts[0]}
        <span className="text-[#0078ff]">{highlightWord}</span>
        {parts[1]}
      </>
    );
  };

  return (
    <section
      className={cn(
        'relative overflow-hidden',
        isLight ? 'bg-white' : 'bg-black',
        sizes[size],
        className
      )}
    >
      {/* ninito style decorative gradient - subtle */}
      {!isLight && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-[#09f]/5 rounded-full blur-3xl" />
        </div>
      )}

      <div className="relative ninito-container">
        <div className={cn(
          'max-w-[960px]',
          align === 'center' && 'mx-auto text-center'
        )}>
          {/* Badge - ninito style */}
          {badge && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <span className="ninito-badge ninito-badge-blue">
                {badge}
              </span>
            </motion.div>
          )}

          {/* Subtitle - ninito style uppercase */}
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={cn(
                'text-sm font-medium uppercase tracking-wider mb-4',
                isLight ? 'text-[#0078ff]' : 'text-[#0078ff]'
              )}
            >
              {subtitle}
            </motion.p>
          )}

          {/* Title - ninito exact styling */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={cn(
              'text-[40px] md:text-[52px] lg:text-[72px] font-semibold leading-[1.05] tracking-[-0.03em]',
              isLight ? 'text-black' : 'text-white'
            )}
          >
            {renderTitle()}
          </motion.h1>

          {/* Description - ninito style */}
          {description && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={cn(
                'mt-6 text-lg md:text-xl max-w-[680px] mx-auto leading-relaxed',
                isLight ? 'text-[#666]' : 'text-[#999]'
              )}
            >
              {description}
            </motion.p>
          )}

          {/* Children (CTA buttons, etc) */}
          {children && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 flex flex-wrap items-center justify-center gap-4"
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
